import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CollectionChatSource } from './types';
import { cn } from '@/lib/utils';

interface SourceDetailsSidebarProps {
  sources: CollectionChatSource[];
  highlightedIndex: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SourceDetailsSidebar = ({
  sources,
  highlightedIndex,
  isOpen,
  onClose,
}: SourceDetailsSidebarProps) => {
  if (!sources || sources.length === 0) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/20 transition-opacity z-40',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={cn(
          'fixed top-0 right-0 h-full w-full md:w-[520px] bg-background border-l shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className='sticky top-0 z-10 flex items-center justify-between border-b bg-background px-6 py-4'>
          <div className='flex items-center gap-2'>
            <h2 className='text-lg font-semibold'>Sources</h2>
            <span className='text-sm text-muted-foreground'>
              ({sources.length} {sources.length === 1 ? 'result' : 'results'})
            </span>
          </div>
          <Button
            variant='ghost'
            size='sm'
            onClick={onClose}
            className='h-8 w-8 p-0'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>

        <div className='px-6 py-6 space-y-6'>
          {sources.map((source, index) => (
            <div
              key={`${source.url ?? source.external_id ?? index}`}
              id={`source-${index}`}
              className={cn(
                'rounded-lg border p-5 space-y-4 transition-colors',
                highlightedIndex === index
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'bg-background',
              )}
            >
              {/* Source Number Badge */}
              <div className='flex items-start gap-3'>
                <span className='inline-flex items-center justify-center h-7 px-2.5 rounded-md border border-primary/30 bg-primary/10 text-sm font-semibold text-primary shrink-0'>
                  [{index + 1}]
                </span>
                {source.title && (
                  <h3 className='text-base font-semibold text-foreground leading-tight'>
                    {source.title}
                  </h3>
                )}
              </div>

              {/* URL */}
              {source.url && (
                <a
                  href={source.url}
                  target='_blank'
                  rel='noreferrer'
                  className='inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline'
                >
                  View original source →
                </a>
              )}

              {/* Content or Snippet */}
              {(source.content || source.snippet) && (
                <details className='text-xs'>
                  <summary className='cursor-pointer font-semibold text-muted-foreground hover:text-foreground'>
                    Content
                  </summary>
                  <div className='mt-2 rounded-md border bg-muted/30 px-3 py-2.5 text-sm text-foreground leading-relaxed'>
                    {source.content || source.snippet}
                  </div>
                </details>
              )}

              {/* Metadata */}
              {(source.external_id ||
                source.category ||
                source.datetime ||
                source.index_time ||
                source.score !== undefined) && (
                <details className='text-xs'>
                  <summary className='cursor-pointer font-semibold text-muted-foreground hover:text-foreground'>
                    Metadata
                  </summary>
                  <div className='mt-2 rounded-md border bg-muted/20 px-3 py-2.5 space-y-1.5'>
                    {source.external_id && (
                      <div className='flex gap-2'>
                        <span className='font-semibold text-foreground min-w-[90px]'>
                          External ID:
                        </span>
                        <span className='text-muted-foreground break-all'>
                          {source.external_id}
                        </span>
                      </div>
                    )}
                    {source.category && (
                      <div className='flex gap-2'>
                        <span className='font-semibold text-foreground min-w-[90px]'>
                          Category:
                        </span>
                        <span className='text-muted-foreground'>
                          {source.category}
                        </span>
                      </div>
                    )}
                    {source.datetime && (
                      <div className='flex gap-2'>
                        <span className='font-semibold text-foreground min-w-[90px]'>
                          Datetime:
                        </span>
                        <span className='text-muted-foreground'>
                          {source.datetime}
                        </span>
                      </div>
                    )}
                    {source.index_time && (
                      <div className='flex gap-2'>
                        <span className='font-semibold text-foreground min-w-[90px]'>
                          Index Time:
                        </span>
                        <span className='text-muted-foreground'>
                          {source.index_time}
                        </span>
                      </div>
                    )}
                    {source.score !== undefined && (
                      <div className='flex gap-2'>
                        <span className='font-semibold text-foreground min-w-[90px]'>
                          Score:
                        </span>
                        <span className='text-muted-foreground'>
                          {source.score.toFixed(4)}
                        </span>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Additional Metadata */}
              {source.metadata && Object.keys(source.metadata).length > 0 && (
                <details className='text-xs'>
                  <summary className='cursor-pointer font-semibold text-muted-foreground hover:text-foreground'>
                    Additional metadata
                  </summary>
                  <div className='mt-2 rounded-md border bg-muted/20 px-3 py-2 space-y-1 font-mono'>
                    {Object.entries(source.metadata).map(([key, value]) => (
                      <div key={key} className='flex gap-2'>
                        <span className='text-muted-foreground'>{key}:</span>
                        <span className='text-foreground break-all'>
                          {JSON.stringify(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
